import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import conductor from 'scoreboard/components/conductor'
import requiresOrg from 'scoreboard/components/requires-org'
import layoutManager from 'scoreboard/components/layout-manager'
import mobile from 'scoreboard/components/mobile'
import projects from 'scoreboard/projects/projects'
import Project from 'scoreboard/projects/project'
import ProjectsNav from 'scoreboard/projects/projects-nav'
import OrganizationsNav from 'scoreboard/organizations/organizations-nav'
import sections from 'scoreboard/components/sections'
import dialogStack from 'scoreboard/components/dialog-stack'
import i18n from 'scoreboard/components/i18n'
import ImagePicker from 'scoreboard/components/image-picker'
import globals from 'scoreboard/components/globals'
import appDisplay from 'scoreboard/components/app-display'
import TreeHoverExpandContract from 'scoreboard/components/tree-hover-expand-contract'
import Tooltip from 'scoreboard/components/tooltip'
import { isV5, isV6 } from 'scoreboard/components/version'
/**
 * The MenuManager controls the movements and synchronization of all the menu's to the
 * left of the display pane. There is a close relationship between what happens inside the
 * .project-organization-menu and the nav/org/project panes, and the interactions related
 * to those components live here in this file.
 */
class MenuManager {

	/**
	 * main.js invokes this but tests can opt not to so that they can inject
	 * their own elements as required.
	 */
	readDOM() {
		this.navPane = $('.nav-pane')
		this.organizationPane = $('.org-pane')
		this.headPane = $('.head-pane')
		this.projectOrganizationMenu = $('.project-organization-menu')
		this.projectPane = $('.project-pane')
		this.browseProjects = $('.browse-projects')
		this.browseOrganizations = $('.browse-organizations')
		this.mobileOrganizationSelector = $('.mobile-organization-selector')
		this.mobileProjectSelector = $('.mobile-project-selector')
		this.body = $('body')
		this.topNav = $('.top-pane')
	}

	setup() {
		layoutManager.toggleTransitions(false)
		if (isV5()) {
			this.projectNav = new ProjectsNav({
				el: this.projectPane,
				treeType: 'dark',
				id: projects.id
			})

			this.organizationNav = new OrganizationsNav({
				el: this.organizationPane,
				section: sections.default(this.headPane.find('ul li.is-selected').data('section')),
				treeType: 'dark'
			})

			this.toggleProjectMenu(projects.isVisible())

			layoutManager.changeLayoutForUri().then(() => {
				this.renderNav({show: false})
			})
			layoutManager.toggleTransitions(true)
		}

		if (isV6()) {
			this.setOrganizationsNav()
		}
		// This holds onto a section when the user in in the midst of selecting
		// a project and org after they clicked a new section and no previous
		// ids were chosen, or after they changed the project and have not yet
		// selected the org. (#24541)
		this.navigatingToSection = undefined
	}

	setOrganizationsNav() {
		this.organizationNav = new OrganizationsNav({
			section: sections.default(this.headPane.find('ul li.is-selected').data('section')),
			treeType: 'dark'
		})
		this.renderNav()
	}

	showOrganizationsNavTooltip() {
		this.setOrganizationsNav()
		this.orgNavTooltip = new Tooltip({
			content: this.organizationNav.$el,
			attachTo: this.topNav.find('.org-nav-toggle'),
			placement: 'above',
			arrow: 'left',
			closeOnDialogOpen: false,
			extraClass: 'org-nav-tooltip is-dark'
		}).render()
	}

	selectOrganization() {
		// TODO: Store the last visited section in the session storage.
		// If no section is found, default to a specified section.
		this.organizationNav.applyNodeSelection('scorecards')
	}

	showProjectsNavTooltip() {
		this.projectNav = new ProjectsNav({
			treeType: 'dark',
			id: projects.id
		})
		this.renderNav()
		this.projectNavTooltip = new Tooltip({
			content: this.projectNav.$el,
			attachTo: this.topNav.find('.library-toggle'),
			arrow: 'left',
			placement: 'above',
			closeOnDialogOpen: false,
			extraClass: 'org-nav-tooltip is-dark'
		}).render()
	}

	/**
	 * Shows or hides the organization pane based on whether it's currently showing.
	 * @param {Event} e The jquery click event
	 */
	_browseOrganizations(e) {
		if (this.organizationPaneVisible()) {
			this.closingBrowseOrganizations = true
			this.hideOrganizationPane()
		} else {
			this.openingBrowseOrganizations = true
			this.showOrganizationPane()
		}

		this.trigger('browse:organizations', e)
	}

	/**
	 * Shows or hides the project pane based on whether it's currently showing.
	 * @param options.section The section the user selected which mandated a
	 * a project and org selection.  Overrides `conductor.section()`
	 * TODO: Remove when V5 is no longer supported
	 */
	_toggleProjectPane(options = {}) {
		if (this.projectPaneVisible()) {
			this.closingBrowseProjects = true
			this.hideProjectPane()
		} else {
			this.openingBrowseProjects = true
			this.showProjectPane(options)
		}

		this.trigger('browse:projects', e)
	}

	setupTreeExpander() {
		delete this.navTreeExpander

		// Setup the navTreeExpander which handles the expanding and contracting of
		// the navPane when tree nodes with long labels are moused over.
		this.navTreeExpander = new TreeHoverExpandContract({
			el: this.navPane,
			canExpandTree:  () => {
				// If this.preventNavExpand is true, if we're already resizing
				// the nav pane manually via the drag handle, or if we're in the
				// mobile orientation, don't expand the tree.
				if (this.preventNavExpand ||
					this.navPane.hasClass('is-resizing') ||
					mobile.isMobileOrientation()) {
					return false
				}

				return true
			},
			canContractTree: () => {
				// Prevent misfires which may occur while moving the mouse when the
				// project/org pane is visible or deploying #24537.
				if (this.projectPaneVisible() || this.organizationPaneVisible()) {
					return false
				}

				return true
			},
			expandConstraint: () => this.body.width() - constants.EXPAND_COLLAPSE_EXIT_SPACE,
			expandMargin: () => this.headPane.width(),
			treeContractBoundary: '.nav-pane-content',
			clearWidthAfterContract: false
		})

		// Listen for when  the expander is just about to start expanding the tree.
		// At that time, also expand the projectOrganizationMenu so that it stays in
		// sync with the nav pane.
		this.navTreeExpander.on('startExpand', (expandWidth, originalWidth) => {
			// Log the original nav pane width for when the nav contracts again
			this.originalNavPaneWidth = originalWidth

			// Prevent https://github.com/SpiderStrategies/Scoreboard/issues/27756
			if (conductor.section() == 'dashboards') {
				this.navPane.trigger('expanded', this.originalNavPaneWidth)
			}
			if (isV5()) {
				this.projectOrganizationMenu
					.addClass('is-expanding')
					.css('width', expandWidth + this.headPane.width())
			}
			// Raise an event so that components in the display pane can adjust
			// when the nav pane expands over top them (ex. remove tooltips).
			Backbone.trigger('navPane:expanded')
		})

		// Keep the projectOrganizationMenu in sync also when the navTreeExpander
		// is beginning to contract.
		if (isV5()) {
			this.navTreeExpander.on('startContract', () => {
				if (!this.navPane.hasClass('is-delaying-collapse')) {

					let projectOrganizationWidth
					if (this.headPane.hasClass('is-collapsed')) {
						projectOrganizationWidth = constants.HEAD_PANE_COLLAPSED_WIDTH
							+ this.originalNavPaneWidth
					} else {
						projectOrganizationWidth = this.headPane.width()
							+ this.originalNavPaneWidth
					}

					this.projectOrganizationMenu
						.removeClass('is-expanding')
						.css('width', projectOrganizationWidth)
				}
			})
		}

		this.navTreeExpander.on('endContract', () => {
			delete this.originalNavPaneWidth
			this.navPane.trigger('collapsed')
		})

		// When the head/nav pane is collapsed, we want to contract the navTreeExpander.
		this.navPane.on('toggleCollapse', () => this.navTreeExpander.contractTree(true))
		this.headPane.on('toggleCollapse', () => this.navTreeExpander.contractTree(true))
	}

	/**
	 * Render the nav panes (project and organizations nav). This function also
	 * sets up the treeExpander to work with the new navView gardener (if it exists).
	 * @param  {Boolean} options.show If true, the project and organization
	 *     nav's will be shown after being rendered.
	 */
	renderNav(options = {show: true}) {
		if (layoutManager.hasOrgNav()) {
			// not passing in an ID to select because the app is loading, so
			// the org ID probably isn't known
			this.navRenderOptions = _.extend({}, options, {
				el: this.organizationPane,
				section: sections.default(this.headPane.find('ul li.is-selected').data('section')),
				treeType: 'dark'
			})

			// Turn off animations before we render the panes/menus and hook up our
			// event listeners. This will make sure the DOM setup is snappy.
			layoutManager.toggleTransitions(false)
			this.projectNav?.render()
			this.organizationNav?.render()
			if (this.navRenderOptions.show) {
				this.projectNav?.show()
				this.organizationNav?.show(this.navRenderOptions)
			}
			this.organizationNav?.toggleEdit(false)
			this.updateProjectMenu()
			this.addEventListeners()
			layoutManager.toggleTransitions(true)
		}

		// After the nav tree has been rendered, go ahead and setup the
		// TreeHoverExpandContract so we can expand/contract the nav pane as needed.
		// Only do this for navViews that have a gardener (not home/admin).
		// Note: exports don't have a nav view
		if (!_.isNil(_.get(appDisplay.navView(), 'gardener'))) {
			appDisplay.navView().gardener._onceTreeRendered(() => this.setupTreeExpander())
		}
	}

	/**
	 * Change the app to operational mode and show the menus accordingly.
	 * To be called after an admin has configured the server
	 * (Adding the 1st database and/or associating a license to the DB)
	 */
	operationalize() {
		globals.mode = constants.MODE_OPERATIONAL

		// Callers should make sure the license/db is valid, but if not
		// then globals.setMode will just set to configuration mode
		// so we have to make sure we should in fact show the menus
		if (globals.mode == constants.MODE_OPERATIONAL) {
			// Now that we're in operational mode, render the navs (#21922)
			// But, we're still in the admin section so don't show them
			menuManager.renderNav({show: false})
		}
	}

	/**
	 * Add event listeners for the menu-manager.
	 */
	addEventListeners() {
		if (isV5()) {
			this._addPointerEventListers()
		}
		this._addGlobalEventListeners()
		this._addNavPaneListeners()
		this._overrideTransitionendListeners()
	}

	/**
	 * Add event listeners which are at the level of global Backbone events
	 */
	_addGlobalEventListeners() {
		this.listenTo(Backbone, 'config:enableProjects', () => this.toggleProjectMenu(projects.isVisible()))

		// If current project label/image changes update it
		this.listenTo(Backbone, 'edit:domain-object', model => {
			if (model instanceof Project && model.id == projects.id) {
				this.updateProjectMenu()
			}
		})

		// Keep an ear out for when projects are created in order to update the label/image
		this.listenTo(Backbone, 'add:domain-object', model => {
			if (model instanceof Project) {
				this.checkAndResetMenuLabels()
			}
		})

		// When a tree has been marked empty (all nodes have been removed),
		// wait for the tree to fully reload and then reset menu labels.
		this.listenTo(Backbone, 'empty:tree', () => process.nextTick(() => {
			// If the projects are active and the projects nav is empty
			let projectsCleared = projects.isVisible() && _.isUndefined(projects.id)
			// If the org tree was just emptied
			let organizationsCleared = this.organizationNav?.isEmpty()

			if (projectsCleared || organizationsCleared) {
				var dismissPanes = () => {
					this.hideProjectPane()
					this.hideOrganizationPane()
					this.checkAndResetMenuLabels()
					// If all projects were deleted, we need to hide the whole .project-organization-menu
					// because it'll just be blank. When the user clicks on a section which
					// requires an org, the .project-organization-menu will reappear
					if (projects.isVisible() && this.projectNav?.isEmpty()) {
						layoutManager.toggleProjectOrganizationMenu(false)
					}
					this.stopListening(Backbone, 'empty:dialogStack')
				}
				// The situation at this point is that we know that we need to close
				// both the project (if projects are enabled) and organization panes because
				// either all organizations have been deleted or all projects have been deleted.
				// We can't just this.hideProjectPane()/hideOrganizationPane() quite yet because,
				// at this point, the strong-confirm-prompt dialog is likely still visible.
				// So to mitigate that, check if there's still a dialog visible and wait for the dialogs
				// to be dismissed before hiding the panes.
				if (dialogStack.isDialogVisible()) {
					this.listenTo(Backbone, 'empty:dialogStack', () => dismissPanes())
				} else {
					dismissPanes()
				}

				// Set the global orgId to undefined since there are no orgs
				globals.orgId = undefined
			}

			this.checkAndResetMenuLabels()
		}))
	}

	/**
	 * Add event listeners having to do with the project and organization nav panes.
	 */
	_addNavPaneListeners() {
		// When the projectNav picks up a project change event (push notification, page load,
		// bookmark navigation, etc), pass it on to this._onProjectChange. It's important
		// to rely on the projectNav here and not projects.js directly because that'll result
		// in some circular logic and some functions getting called more than once.
		this.listenTo(this.projectNav, 'change:project', id => this._onProjectChange(id))

		// When a project is selected (not changed, the project isn't actually
		// changed until after an org is selected), the project image/label is
		// switched to reflect the newly selected project and the org pane is
		// deployed so an org can be selected.
		this.listenTo(this.projectNav, 'select:project', id => this._onProjectSelect(id))

		// When the oranizationNav triggers a change:org event, that means a new org has been chosen.
		// This can be triggered a number of ways, see organizations-nav.js:_changeOrg for a list of those ways.
		this.listenTo(this.organizationNav, 'change:org', args => this._onOrganizationChange(args))

		// When an org is selected, that means the 'select' button has been clicked on an organization
		// node in the org pane. This essencially triggers a change:org event but it's different in that
		// it first even if the org hasn't been changed (ex. clicked on the current org we're in)
		this.listenTo(this.organizationNav, 'select:org', id => this._onOrganizationSelect(id))

		this.organizationPane.on('toggleCollapse end', () => {
			// Only run if the org pane is already visible
			if (this.organizationPaneVisible()) {
				// Have to make sure the tree is loaded before any width calculations
				process.nextTick(() => {
					this.showPane({
						class: 'organization',
						activePane: this.organizationPane,
						passivePane: this.projectPane,
						hidePassivePane: this.hideProjectPane,
						nodeClicked: true
					})
				})
			}
		})
	}

	/**
	 * Add event listeners having to do with pointer events
	 * TODO: Remove when V5 is no longer supported
	 */
	_addPointerEventListers() {
		this.browseOrganizations.on('click', e => this._browseOrganizations(e))
		this.mobileOrganizationSelector.on('click', e => this._browseOrganizations(e))
		this.browseProjects.on('click', () => this._toggleProjectPane())
		this.mobileProjectSelector.on('click', () => this._toggleProjectPane())

		this.organizationPane.on('click', '.node', () => this.showPane({
			class: 'organization',
			activePane: this.organizationPane,
			passivePane: this.projectPane,
			hidePassivePane: this.hideProjectPane,
			nodeClicked: true
		}))

		/**
		* When hovering over the org pane tree, the org pane needs to have classes ready for transitioning
		* its width. But there are other transitions needed when toggling the org-pane that will conflict if
		* these transitions are still attached. So we need to remove them before the toggle occors.
		*/
		this.projectOrganizationMenu.on('mouseenter', () => {
			if (!mobile.isMobileOrientation()) {
				this.organizationPane.removeClass('is-transition-ready')
			}
		})

		// Setup listeners for when we mouseover the .browse-projects menu toggler.
		// When this is hovered over, the project portion of the menu expands out and
		// it's returned to it's collapsed size when the mouse leaves
		this.browseProjects.on('mouseenter', () => {
			// The expand-on-mouseenter feature of the .browse-projects shouldn't happen
			// if the projectOrganizationMenu is overextend or if there are no projects
			// in the tree to chose from, or if we're in the mobile orientation.
			if (!this.projectMenuVisible || this.projectOrganizationMenuOverextended ||
				(this.projectNav?.isEmpty() || this.browseOrganizations?.hasClass('is-disabled')) ||
				mobile.isMobileOrientation()) {
				return
			}

			this.expandProjectToggler()
		})
		this.browseProjects.on('mouseleave', () => this.collapseProjectToggler())
	}

	/**
	 * To be extra careful about our dependence upon `transitionend` triggers,
	 * here, we stopPropagation() for any transitionend triggers on child
	 * elements we don't need to listen to lest those events bubble up to
	 * a parent element we're watching and wreak havoc.
	 */
	_overrideTransitionendListeners() {
		this.browseOrganizations.on('transitionend', e => e.stopPropagation())
		this.browseProjects.on('transitionend', e => e.stopPropagation())
		this.projectOrganizationMenu.on('transitionend', e => e.stopPropagation())
	}

	/**
	 * When a project is 'changed', that means it was first 'selected' from the project nav and
	 * then an org was chosen from the org nav. This new project is now the officially chosen
	 * project we are currently viewing in the app.
	 */
	_onProjectChange() {
		projects.pendingProjectId = undefined
		this.updateProjectMenu()
		this.updateOrganizationPane(_.extend({}, this.navRenderOptions,
			this._constructOrgPaneOptions()))
			.then(() => this.checkAndResetMenuLabels())
		this.projectNavTooltip?.remove()
	}

	/**
	 * When a project is 'selected', that means its 'select' tree node button
	 * was pressed, and we should be moving on to selecting an org. The 'selected'
	 * project is not the official project yet and won't be until an org is chosen.
	 * @param {Number} id the id of the project which was just selected
	 */
	_onProjectSelect(id) {
		projects.pendingProjectId = id

		if (_.isNil(this.previousOrganization)) {
			this.previousOrganization = _.invoke(this.organizationNav?.gardener.tree, 'get', globals.orgId)
		}

		globals.orgId = undefined

		let selectedProject = projects.get(id)
		this.updateOrganizationMenu('')
		this.updateProjectMenu(selectedProject)
		this.showOrganizationPane(_.extend({}, this.navRenderOptions,
			this._constructOrgPaneOptions(selectedProject.id)))
		this.projectNavTooltip?.remove()
	}

	/**
	 * @param {Object} [options.orgNode] the tree node which was just selected, thus changing the org
	 * @param {Boolean} [options.manualSelect=false] see organizations-nav.js:_changeOrg()
	 */
	_onOrganizationChange({ orgNode, manualSelect = false }) {
		// If there's a pending project and this was a manualSelect, that means this org change event was
		// due to the user selecting an org, completing the process of switching projects
		if (!_.isNil(projects.pendingProjectId) && manualSelect) {
			projects.id = projects.pendingProjectId
			projects.pendingProjectId = undefined

			// #22903 do NOT mess with the nav pane here
			if (manualSelect) { // see organizations-nav#_changeOrg
				projects.postActiveProject()
			}
		}

		// If the org has been changed and there's no selected node, reset its menu label.
		// This needs to go after the pendingProjectId/manualSelect check above so that an org
		// change while in the process of selecting a project is handled properly #38500.
		if (_.isUndefined(orgNode)) {
			this.checkAndResetMenuLabels()
			return
		}

		// #25941 Once an org is selected, conductor.section() will be available
		// and must take precedence over the former value stored in this.navigatingToSection
		// which is only needed when no org (section) has yet been selected, thus
		// affecting the route and making conductor.section available for use.
		if (manualSelect) {
			this.navigatingToSection = undefined
			delete this.previousOrganization
			this.hideOrganizationPane()
			this.updateOrganizationMenu(orgNode.label)
		}

		if (_.isNil(projects.pendingProjectId) && !manualSelect) {
			this.updateOrganizationMenu(orgNode.label)
		}
	}

	/**
	 * When an org has been selected, that means it's 'select' node has been clicked.
	 * @param {Number} id The id of the org which was selected
	 */
	_onOrganizationSelect(id) {
		// If there's no pending project and this orgNode is for the org we're
		// already displaying, close the org menu.
		if (_.isNil(projects.pendingProject) && globals.orgId == id) {
			if (isV5()) {
				this.hideOrganizationPane()
			}
			if (isV6()) {
				this.orgNavTooltip?.remove()
			}
		}

	}

	/**
	 * Builds the options to pass to the orgPane after a project selection/change.
	 *
	 * @param projectId The projectId to use for the organization gardener.  The
	 * default is the currently selected project, but you can pass in a different
	 * projectId if dealing with a newly or previously selected project.
	 * @private
	 */
	_constructOrgPaneOptions(projectId = projects.id) {
		return {
			projectId,
			// 23475 - If user clicked a section that required an org to be
			// selected then that section must be applied instead of wherever
			// they are (e.g. 'home')
			section: this.navigatingToSection || conductor.section()
		}
	}

	// Clears out the contents of the organization project menu label
	// (instead of "Choose an Organization/Project", it'll be blank).
	// TODO: Remove when V5 is no longer supported
	checkAndResetMenuLabels() {
		// If there aren't any projects to select from, clear out the "Choose a Project" verbiage
		let projectNavEmpty = projects.isVisible() && this.projectNav?.isEmpty()
		this.browseProjects.toggleClass('is-disabled', projectNavEmpty)
			.find('.project-name')
			.toggleClass('is-not-displayed', projectNavEmpty)
		if (projectNavEmpty) {
			this.updateProjectMenu(null)
		}

		// If there aren't any organizations to select from, hide the
		// "Choose an Organization" verbiage
		let orgNavEmpty = this.organizationNav?.isEmpty()
		this.browseOrganizations
			.toggleClass('is-disabled', orgNavEmpty)
			.find('.full-name')
			.toggleClass('is-not-displayed', orgNavEmpty)

		// If either the project or organization portion of the projectOrganizationMenu
		// are disabled, apply the .has-disabled-menu class to the container for
		// styling purposes
		if (this.browseOrganizations.hasClass('is-disabled') || this.browseProjects.hasClass('is-disabled')) {
			this.projectOrganizationMenu.addClass('has-disabled-menu')
		} else {
			this.projectOrganizationMenu.removeClass('has-disabled-menu')
		}
	}

	/**
	 * Update the project menu with the current project's info (From projects.js)
	 */
	updateProjectMenu(project) {
		// If no project was passed in, go with the pendingProject, if there's no pendingProject, go
		// with the active projects.current(), if there's no active project, set the project menu to the
		// the "choose a project" state.
		let p = project || projects.pending() || projects.current()

		// There's no passed in, pending, or active project, show "Choose Project"
		if (_.isEmpty(p)) {
			this.browseProjects.addClass('has-placeholder')
			this.browseProjects.find('.project-name').text(i18n.get('common.project.choose'))
			this.organizationPane.find('.project-name-label').text(i18n.get('common.project.choose'))
		} else {
			this.browseProjects.removeClass('has-placeholder')
			this.browseProjects.find('.project-name').text(_.get(p, 'attributes.label'))
			this.organizationPane.find('.project-name-label').text(_.get(p, 'attributes.label'))

			// Update the project menu image/logo
			let logo = ImagePicker.imgSrc(_.get(p, 'attributes.logoFileId'), projects.LOGO)
			var newImage = $(`<img src="${logo}" class="project-image">`)
			this.projectOrganizationMenu.find('.project-image').replaceWith(newImage)
			this.mobileProjectSelector.find('.project-image').replaceWith(newImage.clone())
			if (isV6()) {
				this.topNav.find('.project-image').replaceWith(newImage.clone())
			}
			this.projectsLogo = projects.LOGO
		}
	}

	/**
	 * Toggles whether the project portion of the menu is visible. This corresponds to
	 * whether projects is enabled in the app or not.
	 * @param {Boolean} visible If true, projects are visible in the app and the project portions of our
	 * 		menus should be shown.
	 */
	toggleProjectMenu(visible) {
		layoutManager.toggleTransitions(false)
		this.projectMenuVisible = visible
		this.projectOrganizationMenu.toggleClass('has-project-browser', this.projectMenuVisible)
		this.collapseProjectToggler()
		this.hideProjectPane()
		layoutManager.toggleTransitions(true)
	}

	/**
	 * Sets the title shown in the org portion of the projectOrganizationMenu (non-mobile + mobile).
	 * @param {String} label The label to be shown in the menu. If undefined, the "Choose an Organization"
	 * 		message would be shown.
	 */
	updateOrganizationMenu(label) {
		if (_.isEmpty(label)) {
			if (isV5()) {
				let choose = i18n.get('common.organization.choose')
				this.browseOrganizations.addClass('has-placeholder')
				this.browseOrganizations.find('.full-name').text(choose)
				this.mobileOrganizationSelector.addClass('has-placeholder')
				this.mobileOrganizationSelector.find('span').text(choose)
			}
			if (isV6()) {
				// Should auto choose the last selected org if one is available.
				// Otherwise, it chooses the root org.
			}
		} else {
			if (isV6()) {
				this.topNav.find('.organization-label').text(label)
			}
			if (isV5()) {
				this.browseOrganizations.removeClass('has-placeholder is-disabled')
				this.browseOrganizations.find('.full-name').text(label)
				this.mobileOrganizationSelector.removeClass('has-placeholder is-disabled')
				this.mobileOrganizationSelector.find('span').text(label)
			}
		}
	}

	/**
	 * Shows a given pane (slides it out from under the
	 * projectOrganizationMenu).
	 *
	 * If an org tree contains really long names that are cut off, this will expand the tree
	 * to the length needed to show the node with the longest name.  Issue #21253
	 *
	 * @param {Object} options Contains references to the active & passive (expanding & non-expanding)
	 *     panes as well as params dealing with classes to be appended as well as whether
	 *     this method was called via clicking a node or not.
	 *     This method is used to expand both the project pane and the organization pane.
	 *     The following options are used in the function in order to cut down on extra calculations
	 *     needed in figuring out which panes to adjust.
	 * @param {Object} options.activePane The pane which is to be expanded
	 * @param {Object} options.passivePane The pane which is not being expanded
	 * @param {Function} options.hidePassivePane The function associated with hiding the passivePane
	 * @param {String} options.class The pane-identifying string used in applying the correct expand related classes
	 * @param {Boolean} options.nodeClicked True when the given activePane is being expanded due to a tree node click.
	 *
	 * TODO: Remove when V5 is no longer supported
	 */
	showPane(options = {}) {
		// If there's a dialog present, we shouldn't be moving the menus around
		if (dialogStack.isDialogVisible()) {
			// #21203 if a dialog is in the process of being closed, let it
			// finish it's animation and then show the pane.
			if (dialogStack.dialogs.length == 1 &&
				!dialogStack.peek().isOpen()) {
				dialogStack.peek().on('close', () => this.showPane(options))
			}
			return
		}

		let storedMinWidth = this.orgProjectPaneWidth + constants.TREE_NODE_MASK_WIDTH
		let tree = options.activePane.find('.tree')[0]
		let treeScrollWidth = !_.isNil(tree) ? tree.scrollWidth : 0
		let headNavWidth = this.headPane.width() + this.navPane.width()
		let treeWidth = 0
		if (this.projectOrganizationMenu.hasClass('is-overextended')) {
			treeWidth = treeScrollWidth
		} else {
			treeWidth = treeScrollWidth + constants.TREE_NODE_MASK_WIDTH
		}

		let paneMinWidth = Math.max(headNavWidth, treeWidth)
		let bodyWidth = this.body.width() - constants.EXPAND_COLLAPSE_EXIT_SPACE
		this.orgProjectPaneWidth = Math.min(bodyWidth, paneMinWidth)

		// If showPane() is called from clicking a node, only continue on if the width needs to be adjusted.
		if (options.nodeClicked) {
			if (storedMinWidth >= this.orgProjectPaneWidth) {
				return
			} else {
				this.delayedMenuReveal = false
				this.organizationPane.addClass('is-expanding is-transition-ready')
			}
		}

		// Make sure the .project-organization-menu is visible before we go about the
		// business of showing any panes.
		layoutManager.toggleProjectOrganizationMenu(true)

		// If we're swapping panes while the projectOrganizationMenu is
		// overextended, set this.swapWhileExtended in order to ensure the
		// projectOrganizationMenu stays extended
		if (this.projectOrganizationMenuOverextended && options.passivePane.hasClass('is-visible')) {
			this.swapWhileExtended = true
		} else {
			this.swapWhileExtended = false
		}

		options.hidePassivePane.apply(this)

		var handleShow = () => {
			if (isV5()) {
				this.projectOrganizationMenu.addClass(`${options.class}-menu-opened ${options.class}-menu-opened-appearance`)

				// Store the current width so we can go back to it in case
				// we need to expand it to meet the minimum width
				this.projectOrganizationMenuWidth = this.projectOrganizationMenu.width()

				// The width the orgTitle and organizationPane will be when toggled
				var expandWidth = Math.max(this.projectOrganizationMenuWidth, this.orgProjectPaneWidth)

				options.activePane.css({
					'top': constants.ORG_PANE_TRANSITION_MARGIN*-1,
					'width': mobile.isMobileOrientation() ? '100%' : expandWidth + 'px'
				})

				this.headPane.removeClass('is-visible')
				options.activePane.addClass('is-visible').removeClass('is-out-of-view')
				this.swapWhileExtended = true

				// Highlight the correct mobile-nav menu item
				$('.mobile-nav a').removeClass('is-selected')
				$(`.mobile-nav a.mobile-${options.class}-selector`).addClass('is-selected')

				if (this.delayedMenuReveal) {
					// Append 'delayed-menu-reveal-extended' to let the .projectOrganizationMenu know
					// it's done being extended beyond the collapsed menu area (.is-overextend)
					this.projectOrganizationMenu.addClass('delayed-menu-reveal-extended')
					this.browseOrganizations.css('padding-inline-start', constants.PROJECT_MENU_COLLAPSED_WIDTH)
				}
			}

			if (isV6()) {
				this.showOrganizationsNavTooltip()
			}
		}

		// If the menu area is too narrow, and we're not in the mobile orientation,
		// we'll need to expand or `overextend` the projectOrganizationMenu before we go on to
		// showing the pane.
		if (!mobile.isMobileOrientation() && _.round(this.projectOrganizationMenu.width()) < _.round(this.orgProjectPaneWidth)) {
			// If we're in the delayedMenuReveal mode, that means we're showing the menu's from a
			// totally collapsed state. According to the designs, the project-related parts of the menu, in this case, show up
			// slightly after the rest of thte parts.
			// Things happen in this order:
			// 1. the projectOrganizationMenu is stretched out with the org portion taking up 100% of the width
			// 2. the organizationPane is deployed
			// 3. When the organizationPane is done deploying, the projects portion of the projectOrganizationMenu slides into place.
			this.projectOrganizationMenu
				.toggleClass('is-delayed-menu-reveal', this.delayedMenuReveal === true)
				.addClass('is-overextended')
				.trigger('isOverextended')
			this.projectOrganizationMenuOverextended = true
			if (isV5()) {
				// If menu is being expanded from a node click, expand everything at the same time.
				if (options.nodeClicked) {
					this.orgProjectPaneWidth += constants.TREE_NODE_MASK_WIDTH
					this.adjustMenuWidth(this.orgProjectPaneWidth)
					handleShow()
				} else {
					// Otherwise, expand the menu first before showing the pane.
					this.adjustMenuWidth(this.orgProjectPaneWidth).then(() => handleShow())
				}
			}
			if (isV6()) {
				handleShow()
			}
		} else {
			handleShow()
		}
	}

	/**
	 * Hides a given pane (dimisses it back up into the
	 * projectOrganizationMenu).
	 * @param  {String} pane The name of the pane ('project' || 'organization')
	 *     which will be used to manipulate the correct pane and call the
	 *     correct methods
	 *
	 * TODO: Remove when V5 is no longer supported
	 */
	hidePane(pane) {
		var paneObj = pane == 'project' ? this.projectPane : this.organizationPane
		,	oppositePane = pane == 'project' ? this.organizationPane : this.projectPane

		if (!this[pane + 'PaneVisible']()) {
			return
		}

		if (this.headPane.hasClass('is-displaying-admin')) {
			this.organizationNav?.toggleEditViewState(false)
		}

		// After all is done in hiding the passed in pane, afterHide() is called
		// which handles re-adjusting the projectOrganizationMenu to appear as it should
		// after this particular pane has been dismissed
		var afterHide = thePane => {
			let thePaneIsVisible = thePane.hasClass('is-visible')
			let oppositePaneIsVisible = oppositePane.hasClass('is-visible')
			if (!thePaneIsVisible) {
				this.projectOrganizationMenu.removeClass(`${pane}-menu-opened-appearance`)
				this.collapseProjectToggler()
				thePane.addClass('is-out-of-view')

				if (pane === 'organization') {
					this.organizationPane.removeClass('is-expanding')
				}
			}

			// If we're closing the visible pane and the other isn't in the process of showing,
			// set swapWhileExtended to false so we'll know to collapse the projectOrganizationMenu
			if (!thePaneIsVisible && !oppositePaneIsVisible) {
				this.swapWhileExtended = false
			}

			// If the projectOrganizationMenu is overextended because the menu area was too
			// narrow, adjust it after the pane is done hiding. Skip this step if we're going
			// between the panes while the projectOrganizationMenu is extended because it is going
			// to stay at the current width in that case.
			if (this.projectOrganizationMenuOverextended && !this.swapWhileExtended) {
				var width
				if (thePaneIsVisible) {
					width = thePane.width()
				} else {
					width = this.headPane.width() + (layoutManager.isNavCollapsed() ? 0 : this.navPane.width())
				}
				this.adjustMenuWidth(width)
				this.collapseProjectToggler()
				this.projectOrganizationMenu.removeClass('is-overextended')
				this.projectOrganizationMenuOverextended = false
				this.swapWhileExtended = false
			}

			// If we're hiding a pane without opening the other AND we're in a section
			// that doesn't need to show the .project-organization-menu, be sure to hide it here.
			if ((!thePaneIsVisible && !oppositePaneIsVisible) && !requiresOrg(conductor.section())) {
				layoutManager.toggleProjectOrganizationMenu(false)
			}

			this.projectNav?.toggleEdit(false)
			this.organizationNav?.toggleEdit(false)
			layoutManager.toggleTransitions(false)
			this.projectOrganizationMenu?.removeClass('is-delayed-menu-reveal')
			layoutManager?.toggleTransitions(true)
			this.delayedMenuReveal = false
			if (isV5()) {
				this.checkAndResetMenuLabels()
			}
		}

		// If the pane's already hidden or if there's a dialog present, don't do anything
		if (!paneObj.hasClass('is-visible') || dialogStack.isDialogVisible()) {
			return
		}

		this.projectOrganizationMenu.removeClass(`${pane}-menu-opened`)

		var handleHide = () => {
			layoutManager.toggleTransitions(false)
			this.projectOrganizationMenu.removeClass('delayed-menu-reveal-extended')
			layoutManager.toggleTransitions(true)
			afterHide(paneObj)
		}

		if (this.isResizing || !layoutManager.transitionsEnabled()) {
			// Allow an extra tick to finish the hiding of the pane before we manually call afterHide()
			process.nextTick(() => handleHide())
		} else {
			paneObj.one('transitionend', e => {
				e.stopPropagation()
				handleHide()
			})
		}

		// Set the pane to the minium width the nav can be so that it is hidden. Cannot set to 0  because
		// it no longer allows the org pane to keep the current org's scroll position. Issue #9067
		paneObj.removeClass('is-visible is-transition-ready')
			.css({
				'top': (paneObj.height() + constants.ORG_PANE_TRANSITION_MARGIN)*-1,
				'width': constants.HEAD_PANE_COLLAPSED_WIDTH + 'px'
			})

		this.projectOrganizationMenu.removeClass(`${pane}-menu-opened`)
		$('.mobile-nav a:not(.mobile-app-selector)').removeClass('is-selected')

		// If css transitions are disabled, resolve the promise here since
		// the transitionend event won't fire in that case
		if (!layoutManager.transitionsEnabled()) {
			afterHide()
		}
	}

	/**
	 * Closes the appropriate panes based on the state of the menu when the
	 * document is clicked. This is invoked in main.js:documentClick().
	 * @param  {Event} e Click event used to figure out whether the document
	 *     click was contained by one of the panes
	 */
	documentClick(e) {
		// #57461
		// If a dialog exists, such as when creating a new organization, do not
		// close the panes when clicking outside of them.
		if (dialogStack.isDialogVisible()) {
			return
		}

		if (isV6()) {
			// Keep the top-pane project selection in sync with the project pane
			this.updateProjectMenu(projects.current())
		}

		let revertToPreviousOrganization = () => {
			if (!_.isNil(this.previousOrganization)) {
				globals.orgId = this.previousOrganization.id

				this.updateOrganizationMenu(this.previousOrganization?.label)
				this.updateOrganizationPane(_.extend({}, this.navRenderOptions,
					this._constructOrgPaneOptions(projects.current().id)))

				delete this.previousOrganization
			}
		}

		// If this document click event came from clicking the org/project menu to open
		// them, ignore it. This function only has to do with closing the panes.
		if (isV5()) {
			if (this.openingBrowseOrganizations || this.openingBrowseProjects) {
				delete this.openingBrowseOrganizations
				delete this.openingBrowseProjects
				return
			}
		}


		// If neither of the panes are open, there's nothing to do, so return then as well.
		if (!(this.projectPaneVisible() || this.organizationPaneVisible())) {
			// If we've just clicked on the browseOrganizations menu to close the org pane, call
			// revertToPreviousOrganization to make sure we reset the organizationMenu in case
			// we were in the middle of a project change (chose a project but not an org yet).
			if (this.browseOrganizations[0].contains(e.target)) {
				revertToPreviousOrganization()
			}

			return
		}

		// If we clicked inside the org/project pane, there's nothing to do here, those panes
		// handle the click themselves.
		let clickedOrgPane = this.organizationPane[0].contains(e.target),
			clickedProjectPane = this.projectPane[0].contains(e.target)
		if (clickedOrgPane || clickedProjectPane) {
			return
		}

		if (projects.isVisible()) {
			// If projects are enabled and if there's a 'pending' project selected
			// and waiting for us to choose an org, we need to make sure we keep the project/org
			// menu in sync with what's really being shown.

			if (!_.isNil(projects.pending())) {
				projects.pendingProjectId = undefined
				revertToPreviousOrganization()
				this.updateProjectMenu()
			}
		}

		// #56280 only hide the panes if there isn't a dialog open
		if (isV5()) {
			if (!dialogStack.peek()) {
				this.hideOrganizationPane()
				this.hideProjectPane()
			}
		}
	}

	/**
	 * Adjusts the width of the top menu bar to the size it needs to be before
	 * deploying or retracking the org pane. The promise is resolved when the
	 * widening/shortening transition is finished.
	 * @param  {Number} width The desired width of the projectOrganizationMenu
	 */
	adjustMenuWidth(width) {
		return new Promise(resolve => {
			this.projectOrganizationMenu.one('transitionend', e => resolve())
			this.projectOrganizationMenu.css({ width })
		})
	}

	/**
	 * Expands the project toggler (.browse-projects) so that it takes up the
	 * majority of the projectOrganizationMenu when it's hovered over
	 */
	expandProjectToggler() {
		if (!this.projectMenuVisible ||this.projectOrganizationMenu.hasClass('organization-menu-opened')) {
			return
		}

		// In case we were in the delayedMenuReveal mode, nix the related classes since we
		// wont need them after expanding the project toggler
		layoutManager.toggleTransitions(false)
		this.projectOrganizationMenu.removeClass('is-delayed-menu-reveal delayed-menu-reveal-extended')
		layoutManager.toggleTransitions(true)
		this.delayedMenuReveal = false

		// No more than 2/3 the width of the total .projec-organization-menu
		var relativeMaxWidth = (this.projectOrganizationMenu.width() - constants.PROJECT_MENU_COLLAPSED_WIDTH)*.66
		,	width = Math.min($('.browse-projects .project-name').width()
			+ 45 /* 45px gradient compensation */, constants.PROJECT_MENU_MAX_WIDTH, relativeMaxWidth)

		this.browseProjects
			.addClass('is-expanded')
			.css('width', width + constants.PROJECT_MENU_COLLAPSED_WIDTH)

		this.browseOrganizations.css({
			'padding-inline-start': width + constants.PROJECT_MENU_COLLAPSED_WIDTH
		})
	}

	/**
	 * Collapses the project toggler (.browse-projects) so that it goes back to
	 * it's original small size
	 */
	collapseProjectToggler() {
		if (mobile.isMobileOrientation() || this.projectPaneVisible()) {
			return
		}

		// If projects aren't enabled, clean up the added styling possibly
		// added from the last time projects were enabled
		if (!this.projectMenuVisible) {
			this.browseProjects.css('width', 'auto')
			this.browseOrganizations.removeAttr('style')
			return
		}

		this.browseProjects
			.removeClass('is-expanded')
			.css('width', constants.PROJECT_MENU_COLLAPSED_WIDTH)

		this.browseOrganizations.css({
			'padding-inline-start': constants.PROJECT_MENU_COLLAPSED_WIDTH
		})
	}

	/**
	 * Show the project pane if it's not already visible
	 * TODO: Remove when V5 is no longer supported
	 */
	showProjectPane() {
		if (this.projectPaneVisible() || isV6()) {
			return
		}

		this.showPane({
			class: 'project',
			activePane: this.projectPane,
			passivePane: this.organizationPane,
			hidePassivePane: this.hideOrganizationPane
		})

		_.invoke(this.projectNav, 'show')
		this.expandProjectToggler()
		this.checkAndResetMenuLabels()
	}

	/**
	 * Hide the project pane if it's not already hidden
	 * TODO: Remove when V5 is no longer supported
	 */
	hideProjectPane() {
		if (!this.projectPaneVisible()) {
			return
		}

		this.hidePane('project')

		// If we're hiding the project pane and it's empty, that means there's nothing
		// to choose from and all projects/orgs have been deleted, so hide the .project-organization-menu
		if (this.projectNav?.isEmpty()) {
			layoutManager.toggleProjectOrganizationMenu(false)
		}
	}

	/**
	 * @param options.section
	 * @param options.projectId
	 */
	showOrganizationPane(options = {}) {
		// Can't make an org tree request without a projectId (passed in or existing)
		if (projects.isVisible()
			&& _.isUndefined(options.projectId)
			&& _.isUndefined(projects.id)) {
			return
		}

		// If we're not in the mobile layout,the projectPane is not visible,
		// and both the nav and head panes are collapsed, set this.delayedMenuReveal
		// in order that the correct special animation can kick in for that situation.
		// This extra animation should only be used when the projects feature
		// is active.
		if (!mobile.isMobileOrientation() && !this.projectPaneVisible() &&
			layoutManager.isHeadCollapsed() && layoutManager.isNavCollapsed() &&
			this.projectMenuVisible) {
			this.delayedMenuReveal = true
		}

		this.showPane({
			class: 'organization',
			activePane: this.organizationPane,
			passivePane: this.projectPane,
			hidePassivePane: this.hideProjectPane
		})

		this.collapseProjectToggler()
		return this.updateOrganizationPane(options)?.then(nav => {
			this.checkAndResetMenuLabels()
			// We've just finished showing the org nav. If there's no global org id set,
			// there are nodes in the org tree to select from, and none of those nodes are
			// selected, go ahead and select that first node.
			if (_.isNil(globals.orgId) &&
				!_.isEmpty(nav.gardener.tree.nodes) &&
				_.isNil(nav.gardener.tree.selected())) {
				nav.gardener.tree.select(nav.$('[data-id]').first().data('id'))
			}
		})
	}

	/**
	 * Hide the organization pane
	 * TODO: Remove when V5 is no longer supported
	 */
	hideOrganizationPane() {
		return this.hidePane('organization')
	}

	/**
	 * Updates the tree and selected org without showing the org pane.
	 */
	updateOrganizationPane(options) {
		let {section} = options
		if (section === 'home') {
			// https://github.com/SpiderStrategies/Scoreboard/issues/28494#issuecomment-674088902
			return Promise.resolve()
		}
		if (!section) {
			section = _.defaultTo(this.navigatingToSection, conductor.section())
		}
		return this.organizationNav?.show(_.extend({}, options, {section}))
	}

	/**
	 * Determine whether the project pane is visible.
	 * @return {Boolean} True if the project pane is visible, else false.
	 */
	projectPaneVisible() {
		return this.projectPane.hasClass('is-visible')
	}

	/**
	 * Determine whether the project pane is visible.
	 * @return {Boolean} True if the organization pane is visible, else false.
	 */
	organizationPaneVisible() {
		return this.organizationPane.hasClass('is-visible')
	}

	/**
	 * Updates the menu's as needed accorrding to the head pane section which was just selected.
	 * @param {String} section The section which is being navigated to
	 * @returns true if all that needed to take place to handle the section change was done
	 */
	handleSectionChange(section) {
		// If there's just one org to choose from in the org tree, then set that as globals.orgId since
		// that's the only place to navigate to
		if (this.organizationNav
			&& _.get(this.organizationNav.gardener, 'stream.finished')
			&& Object.keys(this.organizationNav.gardener.tree.nodes).length == 1) {
			globals.orgId = this.organizationNav.gardener.tree.get().id
		}

		if (requiresOrg(section)) {
			if (projects.isVisible() && !projects.id) {
				if (!this.projectPaneVisible()) {
					this.navigatingToSection = section
					this._toggleProjectPane({section})
				}
				return true
			} else if (!globals.orgId) {
				// Since there is no global orgId, check to see if the organizationPane is showing
				// because we'll need to select an org before we do any navigating to the section we just clicked.
				// If the organizationPane isn't showing already then show it and reset it's organization label so
				// that it shows the "Choose an Organization" verbage.
				if (!this.organizationPaneVisible()) {
					this.navigatingToSection = section
					this.showOrganizationPane({ section })
				}
				this.updateOrganizationMenu()
				return true
			}
		}
	}

	/**
	 * Resize is called whenever the window or display pane is resized (using the nav pane resize handle)
	 */
	resize(resetMenuWidths) {
		this.isResizing = true
		this.hideOrganizationPane()
		this.hideProjectPane()
		// Resize trees in both the org nav and project nav
		this.organizationNav?.resize()
		this.projectNav?.resize()
		this.projectOrganizationMenu
			.removeClass('menu-opened')
			.css('width', () => {
				let head = this.headPane.hasClass('is-collapsed') ?
					constants.HEAD_PANE_COLLAPSED_WIDTH : this.headPane.width()
				let nav = this.navPane.hasClass('is-collapsed') ?
					0 : this.navPane.width()
				return head + nav
			})
		this.isResizing = false
		// Resize could be called for a number of reasons, but one of them is
		// that the nav pane resizer could have been dragged. If that happens, the
		// navPane size is now different so we need to update the navTreeExpander
		// so it has the correct values for its expanding/conracting.
		if (this.navTreeExpander) {
			delete this.navTreeExpander.originalWidth
		}
	}

	changeSectionContent() {
		var newIcon = this.headPane.find('.is-selected svg')
		$('.mobile-app-selector').find('.icon-app')
			.removeAttr('class')
			.addClass('icon-app ' + newIcon.attr('class'))
			.find('use')
			.attr('xlink:href', newIcon.find('use').attr('xlink:href'))
		this.mobileProjectSelector.toggleClass('is-not-displayed',
			!requiresOrg(conductor.section()))
	}
}

_.extend(MenuManager.prototype, Backbone.Events)

/**
 * Changes the icon in the mobile menu app selector (corresponds to the head pane)
 */


var menuManager = new MenuManager()
export default menuManager
export { MenuManager }

menuManager.__filename__ = 'menu-manager.js'
